import { formatPropertyAddress, formatStreetAddress } from '../utils/propertyAddress';
import FleetPropertyDetailActions from './FleetPropertyDetailActions';
import {PropertyListPicker} from './PropertyLists';
import {mapCoordinates} from '../utils/propertyCoordinates';
import PropertyLocationMap from './PropertyLocationMap';
import { sendEmailRequest } from '../services/emailDeliveryService';
import { rememberPropertyPhoto } from '../services/propertyThumbnails';
import { getPropertyShareUrl } from '../utils/propertySharing';
import { usePropertySave } from './PropertySaveProvider';
import React, { useEffect, useRef, useState } from 'react';

import { SITE_CONFIG } from '../config';
import { trackPropertyView } from '../services/analyticsService';

import './PropertyLeadModal.css';

const PropertyDetailModal = ({
  propertyDetail,
  propertyDetailImages,
  imagesLoading,
  onClose,
  user,
  isAdmin,
  likedProperties,
  setLikedProperties,
  senderEmail,
  senderPassword,
  onLeadCaptured
}) => {
  const { favorites, toggleFavorite } = usePropertySave();
  useEffect(() => { setLikedProperties?.(favorites); }, [favorites, setLikedProperties]);

  const [enlargedImage, setEnlargedImage] = useState(null);
  const [enlargedImageIndex, setEnlargedImageIndex] = useState(0);
  const [showCopySuccess, setShowCopySuccess] = useState(false);

  const [showAiStaging, setShowAiStaging] = useState(false);
  const [stagingImage, setStagingImage] = useState(null);
  const [stagingLoading, setStagingLoading] = useState(false);

  const [showEmailSendModal, setShowEmailSendModal] = useState(false);
  const [emailRecipient, setEmailRecipient] = useState('');
  const [emailCustomerName, setEmailCustomerName] = useState('');
  const [emailSubject, setEmailSubject] = useState('');
  const [emailPreviewHtml, setEmailPreviewHtml] = useState('');
  const [emailSending, setEmailSending] = useState(false);
  const [emailGenerating, setEmailGenerating] = useState(false);
  const [emailMode, setEmailMode] = useState('email');
  const [socialCopy, setSocialCopy] = useState(null);
  const trackedListingKeyRef = useRef(null);

  const getValidVirtualTourUrl = (url) => {
    try {
      const parsedUrl = new URL(url);
      return ['http:', 'https:'].includes(parsedUrl.protocol) ? parsedUrl.href : '';
    } catch {
      return '';
    }
  };

  // Lock background scroll while the detail modal is open so the page behind the
  // (viewport-centered) modal doesn't scroll away under it.
  useEffect(() => {
    if (!propertyDetail) return undefined;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prevOverflow; };
  }, [propertyDetail]);

  useEffect(() => {
    const listingKey = propertyDetail?.listing_key;
    if (!listingKey) {
      trackedListingKeyRef.current = null;
      return;
    }
    if (trackedListingKeyRef.current === listingKey) return;

    trackedListingKeyRef.current = listingKey;
    const pathname = window.location.pathname;
    let viewSurface = 'landing_page';
    if (pathname.startsWith('/listing/')) viewSurface = 'direct_listing';
    else if (pathname === '/properties/list') viewSurface = 'property_list';
    else if (pathname === '/search') {
      viewSurface = new URLSearchParams(window.location.search).get('searchLens') === 'true'
        ? 'search_lens'
        : 'search_results';
    } else if (pathname === '/chat') viewSurface = 'chat_results';

    trackPropertyView({ property: propertyDetail, surface: viewSurface });
  }, [propertyDetail]);

  if (!propertyDetail) return null;

  const virtualTourUrl = getValidVirtualTourUrl(propertyDetail.virtual_tour_url);
  const propertyAddress = formatPropertyAddress(propertyDetail);
  const totalBathrooms = propertyDetail.bathrooms_total_integer
    ?? propertyDetail.bathrooms_total
    ?? ((propertyDetail.bathrooms_full || 0) + ((propertyDetail.bathrooms_half || 0) * 0.5));
  const numericLivingArea = Number(String(propertyDetail.living_area || '').replace(/,/g, ''));
  const formattedLivingArea = Number.isFinite(numericLivingArea)
    ? numericLivingArea.toLocaleString()
    : propertyDetail.living_area;
  const isPropertySaved = likedProperties.has(propertyDetail.listing_key);

  const handleLike = () => toggleFavorite(propertyDetail, user);

  const handleAiStaging = async (type) => {
    setStagingLoading(true);
    try {
      const promptResponse = await fetch(`https://tophamtechbeb.com/api/stage/v1/${type}-prompts/random`, {
        headers: { 'X-API-Key': 'treas-api-key-2025' }
      });
      if (!promptResponse.ok) throw new Error('Failed to get staging prompt');
      const promptData = await promptResponse.json();
      const formData = new FormData();
      formData.append('image_url', enlargedImage);
      formData.append('design_prompt', promptData.prompt);
      const stagingResponse = await fetch('https://tophamtechbeb.com/api/stage/v1/stage-room', {
        method: 'POST',
        headers: { 'X-API-Key': 'treas-api-key-2025' },
        body: formData
      });
      if (!stagingResponse.ok) throw new Error('Failed to stage room');
      const stagedBlob = await stagingResponse.blob();
      setStagingImage(URL.createObjectURL(stagedBlob));
      setShowAiStaging(false);
    } catch (error) {
      console.error('Error staging room:', error);
      alert('Failed to stage room. Please try again.');
    } finally {
      setStagingLoading(false);
    }
  };

  return (
    <>
      <div className="expanded-map-modal property-detail-overlay" onClick={onClose}>
        <><FleetPropertyDetailActions home={propertyDetail} user={user} /><div className="property-detail-modal property-detail-modal--premium" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-label={`Property details for ${propertyAddress}`}>
          <script type="application/ld+json">
            {JSON.stringify({
              "@context": "https://schema.org",
              "@type": "RealEstateProperty",
              "name": formatPropertyAddress(propertyDetail),
              "address": {
                "@type": "PostalAddress",
                "streetAddress": formatStreetAddress(propertyDetail),
                "addressLocality": propertyDetail.location?.city,
                "addressRegion": propertyDetail.location?.state,
                "postalCode": propertyDetail.location?.postal_code
              },
              "price": propertyDetail.current_price,
              "priceCurrency": "USD",
              "numberOfRooms": propertyDetail.bedrooms_total,
              "numberOfBathroomsTotal": propertyDetail.bathrooms_full + (propertyDetail.bathrooms_half || 0) * 0.5,
              "floorSize": { "@type": "QuantitativeValue", "value": propertyDetail.living_area, "unitCode": "SQF" },
              "propertyType": propertyDetail.property_type,
              "yearBuilt": propertyDetail.year_built,
              "image": propertyDetailImages,
              "description": propertyDetail.features?.public_remarks,
              "url": `${window.location.origin}/listing/${propertyDetail.listing_key}`
            })}
          </script>
          <div className="property-detail-actions" aria-label="Property actions">
            <button
              className="link-button"
              onClick={() => {
                const url = getPropertyShareUrl(propertyDetail, SITE_CONFIG);
                navigator.clipboard.writeText(url).then(() => {
                  setShowCopySuccess(true);
                  setTimeout(() => setShowCopySuccess(false), 3000);
                }).catch(() => alert('Failed to copy link'));
              }}
              aria-label="Copy property link to clipboard"
            >
              <span aria-hidden="true">↗</span><small>Share</small>
            </button>
            <button
              className={`heart-button ${isPropertySaved ? 'liked' : ''}`}
              onClick={handleLike}
              aria-label={isPropertySaved ? "Remove property from favorites" : "Save property to favorites"}
            >
              <span aria-hidden="true">{isPropertySaved ? '♥' : '♡'}</span><small>{isPropertySaved ? 'Saved' : 'Save'}</small>
            </button>
            {isAdmin && (
              <button
                className="email-send-button"
                onClick={() => {
                  setEmailSubject(`Property Listing - ${formatPropertyAddress(propertyDetail)}`);
                  setEmailRecipient('');
                  setEmailCustomerName('');
                  setEmailPreviewHtml('');
                  setShowEmailSendModal(true);
                }}
                aria-label="Send property email"
              >
                <span aria-hidden="true">✉</span><small>Email</small>
              </button>
            )}

          <PropertyListPicker listingKey={propertyDetail.listing_key} /><button className="close-expanded-map" onClick={onClose} aria-label="Close property details"><span aria-hidden="true">×</span></button>
</div>
          {showEmailSendModal && (
            <div className="email-send-modal" onClick={(e) => e.stopPropagation()}>
              <div className="email-send-modal-content">
                <button className="email-send-modal-close" onClick={() => { setShowEmailSendModal(false); setSocialCopy(null); }}>×</button>
                <div className="email-send-tabs">
                  <button className={emailMode === 'email' ? 'active' : ''} onClick={() => { setEmailMode('email'); setSocialCopy(null); }}>Email</button>
                  <button className={emailMode === 'social' ? 'active' : ''} onClick={() => { setEmailMode('social'); setEmailPreviewHtml(''); }}>Social</button>
                </div>
                {emailMode === 'email' ? (
                  <>
                    {emailPreviewHtml === 'sent' ? (
                      <div style={{textAlign: 'center', padding: '30px 10px'}}>
                        <div style={{fontSize: '40px', marginBottom: '10px'}}>✅</div>
                        <p style={{color: '#28a745', fontWeight: 600, margin: 0}}>Email sent successfully!</p>
                      </div>
                    ) : !emailPreviewHtml ? (
                      <div className="email-send-form">
                        <input type="email" placeholder="Recipient email *" value={emailRecipient} onChange={(e) => setEmailRecipient(e.target.value)} />
                        <input type="text" placeholder="Customer name (optional)" value={emailCustomerName} onChange={(e) => setEmailCustomerName(e.target.value)} />
                        <input type="text" placeholder="Subject *" value={emailSubject} onChange={(e) => setEmailSubject(e.target.value)} />
                        <button
                          disabled={!emailRecipient.trim() || !emailSubject.trim() || emailGenerating}
                          onClick={async () => {
                            setEmailGenerating(true);
                            try {
                              const res = await fetch('https://tophamtechbe.com/api/treas/comm/property-search-preview?format=html', {
                                method: 'POST',
                                headers: { 'Content-Type': 'application/json', 'X-API-Key': 'treas-api-key-2025' },
                                body: JSON.stringify({
                                  customerName: emailCustomerName || undefined,
                                  agentName: SITE_CONFIG.AGENT.NAME,
                                  siteId: SITE_CONFIG.SITE_ID,
                                  listingBaseUrl: `https://${SITE_CONFIG.BASE_URL}`,
                                  searchFilters: { listingKey: propertyDetail.listing_key }
                                })
                              });
                              const data = await res.json();
                              if (data.html) setEmailPreviewHtml(data.html);
                              else alert(data.message || 'No preview generated');
                            } catch (err) { alert('Error generating preview'); }
                            setEmailGenerating(false);
                          }}
                        >
                          {emailGenerating ? 'Generating...' : 'Preview Email'}
                        </button>
                      </div>
                    ) : (
                      <div className="email-send-preview">
                        <iframe title="Email Preview" srcDoc={emailPreviewHtml} style={{width: '100%', height: '300px', border: '1px solid #ddd', borderRadius: '4px'}} />
                        <div className="email-send-actions">
                          <button onClick={() => setEmailPreviewHtml('')}>← Back</button>
                          <button
                            disabled={emailSending}
                            onClick={async () => {
                              setEmailSending(true);
                              try {
                                const formData = new FormData();
                                formData.append('emailaddress', emailRecipient);
                                formData.append('title', emailSubject);
                                formData.append('body', emailPreviewHtml);
                                if (senderEmail) formData.append('email', senderEmail);
                                if (senderPassword) formData.append('emailPassword', senderPassword);
                                const res = await sendEmailRequest('https://tophamtechbe.com/api/treas/comm/send-email', {
                                  method: 'POST',
                                  headers: { 'X-API-Key': 'treas-api-key-2025' },
                                  body: formData
                                });
                                if (res.ok) {
                                  setEmailPreviewHtml('sent');
                                  setTimeout(() => setShowEmailSendModal(false), 2000);
                                } else alert('Failed to send email');
                              } catch (err) { alert('Error sending email'); }
                              setEmailSending(false);
                            }}
                          >
                            {emailSending ? 'Sending...' : 'Send Email'}
                          </button>
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="email-send-form">
                    {!socialCopy ? (
                      <button
                        disabled={emailGenerating}
                        onClick={async () => {
                          setEmailGenerating(true);
                          try {
                            const res = await fetch('https://tophamtechbe.com/api/treas/comm/property-search-preview?format=social', {
                              method: 'POST',
                              headers: { 'Content-Type': 'application/json', 'X-API-Key': 'treas-api-key-2025' },
                              body: JSON.stringify({
                                agentName: SITE_CONFIG.AGENT.NAME,
                                siteId: SITE_CONFIG.SITE_ID,
                                listingBaseUrl: `https://${SITE_CONFIG.BASE_URL}`,
                                searchFilters: { listingKey: propertyDetail.listing_key }
                              })
                            });
                            const data = await res.json();
                            if (data.text) setSocialCopy(data);
                            else alert(data.message || 'No content generated');
                          } catch (err) { alert('Error generating social copy'); }
                          setEmailGenerating(false);
                        }}
                      >
                        {emailGenerating ? 'Generating...' : 'Generate Social Post'}
                      </button>
                    ) : (
                      <div className="social-copy-result">
                        <textarea readOnly value={socialCopy.text} style={{width: '100%', height: '150px', resize: 'none', borderRadius: '6px', border: '1px solid #ddd', padding: '10px', fontSize: '13px', boxSizing: 'border-box'}} />
                        {socialCopy.images?.length > 0 && (
                          <div style={{display: 'flex', gap: '6px', marginTop: '8px', overflowX: 'auto'}}>
                            {socialCopy.images.map((img, i) => (
                              <div key={i} style={{position: 'relative', flexShrink: 0}}>
                                <img src={img} alt="" style={{height: '60px', borderRadius: '4px'}} />
                                <button onClick={() => window.open(img, '_blank')} style={{position: 'absolute', bottom: '2px', right: '2px', background: 'rgba(0,0,0,0.6)', color: '#fff', borderRadius: '4px', padding: '2px 5px', fontSize: '10px', border: 'none', cursor: 'pointer'}}>⬇</button>
                              </div>
                            ))}
                          </div>
                        )}
                        <button style={{marginTop: '10px'}} onClick={() => { navigator.clipboard.writeText(socialCopy.text); }}>📋 Copy Text</button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
          <div className="property-detail-content">
            <div className="property-header">
<div className={`property-overview-grid${mapCoordinates(propertyDetail) ? '' : ' property-overview-grid--no-map'}`}><div className="property-overview-summary">
              {!propertyDetail.isPrivate && (propertyDetail.list_agent_full_name || propertyDetail.list_office_name) && (
                <div className="property-listing-attribution">
                  {propertyDetail.list_agent_full_name && (
                    <div>Listing Agent: {propertyDetail.list_agent_full_name}{propertyDetail.list_agent_mls_id ? ` (${propertyDetail.list_agent_mls_id})` : ''}</div>
                  )}
                  {propertyDetail.list_office_name && (
                    <div>Office: {propertyDetail.list_office_name}</div>
                  )}
                </div>
              )}
              <p className="property-detail-eyebrow">{propertyDetail.property_sub_type || propertyDetail.property_type || 'Southern California residence'}</p>
              <h2>${propertyDetail.current_price?.toLocaleString()}</h2>
              <p className="property-address">{propertyAddress}</p>
              <div className="property-key-facts" aria-label="Property overview">
                {propertyDetail.bedrooms_total != null && <span><strong>{propertyDetail.bedrooms_total}</strong><small>Beds</small></span>}
                {totalBathrooms > 0 && <span><strong>{totalBathrooms}</strong><small>Baths</small></span>}
                {propertyDetail.living_area && <span><strong>{formattedLivingArea}</strong><small>Sq ft</small></span>}
                {propertyDetail.year_built && <span><strong>{propertyDetail.year_built}</strong><small>Built</small></span>}
              </div>
              </div><PropertyLocationMap property={propertyDetail} /></div>
<div className="property-images">
                {imagesLoading ? (
                  <div className="property-images-status">
                    <div style={{display: 'inline-block', marginRight: '8px', animation: 'spin 1s linear infinite', fontSize: '16px'}}>⟳</div>
                    Checking for images...
                  </div>
                ) : propertyDetailImages.length > 0 ? (
                  propertyDetailImages.map((imageUrl, index) => (
                    <div key={index} className="property-detail-image" onClick={() => { setEnlargedImage(imageUrl); setEnlargedImageIndex(index); }}>
                      <img src={imageUrl} alt={`Property ${index + 1}`} onLoad={() => rememberPropertyPhoto(propertyDetail?.listing_key, imageUrl)} />
                    </div>
                  ))
                ) : (
                  <div className="property-images-status property-images-empty">
                    No images available for this property
                  </div>
                )}
              </div>
              {virtualTourUrl && (
                <a
                  className="virtual-tour-cta"
                  href={virtualTourUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <span className="virtual-tour-cta-title">View 3D Virtual Tour</span>
                  <span className="virtual-tour-cta-subtitle">Walk through this home in 360°</span>
                </a>
              )}
              {propertyDetail.features?.public_remarks && (
                <div className="property-description">
                  <h3>Property Description</h3>
                  <p>{propertyDetail.features.public_remarks}</p>
                </div>
              )}
            </div>
            <div className="property-columns">
              <div className="property-column">
                <h3>Property Details</h3>
                <p><strong>Property Type:</strong> {propertyDetail.property_type}</p>
                {propertyDetail.property_sub_type && <p><strong>Property Sub Type:</strong> {propertyDetail.property_sub_type}</p>}
                <p><strong>Listing Key:</strong> {propertyDetail.listing_key}</p>
                <p><strong>Current Price:</strong> ${propertyDetail.current_price?.toLocaleString()}</p>
                <p><strong>List Price:</strong> ${propertyDetail.list_price?.toLocaleString()}</p>
                {propertyDetail.original_list_price && <p><strong>Original List Price:</strong> ${propertyDetail.original_list_price?.toLocaleString()}</p>}
                <p><strong>Living Area:</strong> {propertyDetail.living_area} sq ft</p>
                <p><strong>Building Area:</strong> {propertyDetail.building_area_total} sq ft</p>
                <p><strong>Year Built:</strong> {propertyDetail.year_built}</p>
                {propertyDetail.stories && <p><strong>Stories:</strong> {propertyDetail.stories}</p>}
                {propertyDetail.listing_contract_date && <p><strong>Days on Market:</strong> {Math.floor((Date.now() - new Date(propertyDetail.listing_contract_date)) / 86400000) || 'First Day on Market!'}</p>}
                {propertyDetail.listing_contract_date && <p><strong>Listed Date:</strong> {new Date(propertyDetail.listing_contract_date).toLocaleDateString()}</p>}
                {propertyDetail.prop_sync_last_synced_at && <p><strong>Last Updated:</strong> {new Date(propertyDetail.prop_sync_last_synced_at).toLocaleString()}</p>}
                {propertyDetail.land && (
                  <div>
                    <h3>Land Details</h3>
                    {propertyDetail.land.lot_size_acres && <p><strong>Lot Size:</strong> {propertyDetail.land.lot_size_acres} acres ({propertyDetail.land.lot_size_square_feet?.toLocaleString()} sq ft)</p>}
                    {propertyDetail.land.lot_features && <p><strong>Lot Features:</strong> {propertyDetail.land.lot_features}</p>}
                    {propertyDetail.land.zoning && <p><strong>Zoning:</strong> {propertyDetail.land.zoning}</p>}
                  </div>
                )}
                <h3>Rooms & Bathrooms</h3>
                {propertyDetail.bedrooms_total != null && <p><strong>Bedrooms:</strong> {propertyDetail.bedrooms_total}</p>}
                {propertyDetail.bathrooms_full != null && <p><strong>Full Bathrooms:</strong> {propertyDetail.bathrooms_full}</p>}
                {propertyDetail.bathrooms_half != null && <p><strong>Half Bathrooms:</strong> {propertyDetail.bathrooms_half}</p>}
                {propertyDetail.fireplaces_total != null && <p><strong>Fireplaces:</strong> {propertyDetail.fireplaces_total}</p>}
                {propertyDetail.schools && (
                  <div>
                    <h3>Schools</h3>
                    <p><strong>Elementary:</strong> {propertyDetail.schools.elementary_school}</p>
                    <p><strong>Middle:</strong> {propertyDetail.schools.middle_school}</p>
                    <p><strong>High:</strong> {propertyDetail.schools.high_school}</p>
                  </div>
                )}
              </div>
              <div className="property-column">
                <h3>Features & Amenities</h3>
                {propertyDetail.features?.garage_spaces != null && <p><strong>Garage:</strong> {propertyDetail.features.garage_spaces} spaces</p>}
                {propertyDetail.features?.heating && <p><strong>Heating:</strong> {propertyDetail.features.heating}</p>}
                {propertyDetail.features?.cooling && <p><strong>Cooling:</strong> {propertyDetail.features.cooling}</p>}
                {propertyDetail.features?.water_source && <p><strong>Water Source:</strong> {propertyDetail.features.water_source}</p>}
                {propertyDetail.foundation_details && <p><strong>Foundation:</strong> {propertyDetail.foundation_details}</p>}
                {propertyDetail.roof && <p><strong>Roof:</strong> {propertyDetail.roof}</p>}
                {propertyDetail.sewer && <p><strong>Sewer:</strong> {propertyDetail.sewer}</p>}
                {propertyDetail.features?.appliances && (
                  <div>
                    <h3>Appliances</h3>
                    <p className="feature-list">{propertyDetail.features.appliances.split(',').join(', ')}</p>
                  </div>
                )}
                {propertyDetail.features?.interior_features && (
                  <div>
                    <h3>Interior Features</h3>
                    <p className="feature-list">{propertyDetail.features.interior_features.split(',').join(', ')}</p>
                  </div>
                )}
                {propertyDetail.features?.exterior_features && (
                  <div>
                    <h3>Exterior Features</h3>
                    <p className="feature-list">{propertyDetail.features.exterior_features.split(',').join(', ')}</p>
                  </div>
                )}
                {propertyDetail.features?.fencing && (
                  <div>
                    <h3>Fencing</h3>
                    <p>{propertyDetail.features.fencing}</p>
                  </div>
                )}
                {propertyDetail.association?.association_name && (
                  <div>
                    <h3>HOA Information</h3>
                    <p><strong>Association:</strong> {propertyDetail.association.association_name}</p>
                    {propertyDetail.association.association_fee && <p><strong>HOA Fee:</strong> ${propertyDetail.association.association_fee} {propertyDetail.association.fee_frequency}</p>}
                  </div>
                )}
                {propertyDetail.features?.lots_features && (
                  <div>
                    <h3>Lot Features</h3>
                    <p className="feature-list">{propertyDetail.features.lots_features.split(',').join(', ')}</p>
                  </div>
                )}
                {propertyDetail.features?.patio_and_porch_features && (
                  <div>
                    <h3>Patio & Porch</h3>
                    <p className="feature-list">{propertyDetail.features.patio_and_porch_features.split(',').join(', ')}</p>
                  </div>
                )}
              </div>
            </div>
            {!propertyDetail.isPrivate && (
              <div className="property-mls-footer">
                <img src={SITE_CONFIG.MLS.LOGO_URL} alt={SITE_CONFIG.MLS.SHORT_NAME} style={{height: '32px', objectFit: 'contain'}} />
                <div>
                  <span>{SITE_CONFIG.MLS.DISCLAIMER}</span>
                </div>
              </div>
            )}
          </div>
        </div></>
      </div>
      {enlargedImage && (
        <div className="gallery-overlay" onClick={() => { setEnlargedImage(null); setStagingImage(null); }}>
          <div className="gallery-container" onClick={(e) => e.stopPropagation()}
            onTouchStart={(e) => { e.currentTarget.dataset.touchStartX = e.touches[0].clientX; }}
            onTouchEnd={(e) => {
              const startX = parseFloat(e.currentTarget.dataset.touchStartX);
              const endX = e.changedTouches[0].clientX;
              const diff = startX - endX;
              if (Math.abs(diff) > 50 && propertyDetailImages.length > 1) {
                if (diff > 0) {
                  const nextIndex = enlargedImageIndex < propertyDetailImages.length - 1 ? enlargedImageIndex + 1 : 0;
                  setEnlargedImageIndex(nextIndex);
                  setEnlargedImage(propertyDetailImages[nextIndex]);
                } else {
                  const prevIndex = enlargedImageIndex > 0 ? enlargedImageIndex - 1 : propertyDetailImages.length - 1;
                  setEnlargedImageIndex(prevIndex);
                  setEnlargedImage(propertyDetailImages[prevIndex]);
                }
                setStagingImage(null);
              }
            }}
          >
            <div className="gallery-top-bar">
              <span className="gallery-counter">{enlargedImageIndex + 1} / {propertyDetailImages.length}</span>
              <div className="gallery-top-actions">
                <button className={`gallery-btn gallery-heart ${likedProperties.has(propertyDetail?.listing_key) ? 'liked' : ''}`}
                  onClick={handleLike}
                >{likedProperties.has(propertyDetail?.listing_key) ? '💖' : '🤍'}</button>
                <button className="gallery-btn" onClick={() => setShowAiStaging(true)}>🤖</button>
                <button className="gallery-btn" onClick={() => { setEnlargedImage(null); setStagingImage(null); }}>✕</button>
              </div>
            </div>
            <img src={stagingImage || enlargedImage} alt="Property" className="gallery-image" />
            {stagingImage && (
              <button className="reset-staging-button" onClick={() => setStagingImage(null)}>Original</button>
            )}
            {propertyDetailImages.length > 1 && (
              <>
                <button className="gallery-arrow gallery-prev" onClick={() => {
                  const prevIndex = enlargedImageIndex > 0 ? enlargedImageIndex - 1 : propertyDetailImages.length - 1;
                  setEnlargedImageIndex(prevIndex);
                  setEnlargedImage(propertyDetailImages[prevIndex]);
                  setStagingImage(null);
                }}>‹</button>
                <button className="gallery-arrow gallery-next" onClick={() => {
                  const nextIndex = enlargedImageIndex < propertyDetailImages.length - 1 ? enlargedImageIndex + 1 : 0;
                  setEnlargedImageIndex(nextIndex);
                  setEnlargedImage(propertyDetailImages[nextIndex]);
                  setStagingImage(null);
                }}>›</button>
              </>
            )}
          </div>
        </div>
      )}
      {showAiStaging && (
        <div className="ai-staging-modal" onClick={() => setShowAiStaging(false)}>
          <div className="ai-staging-content" onClick={(e) => e.stopPropagation()}>
            <h3>AI Room Staging</h3>
            <p>Select room type to generate AI staging:</p>
            <div className="staging-buttons">
              <button className="staging-type-button" onClick={() => handleAiStaging('bedroom')} disabled={stagingLoading}>🛏️ Bedroom</button>
              <button className="staging-type-button" onClick={() => handleAiStaging('office')} disabled={stagingLoading}>💼 Office</button>
              <button className="staging-type-button" onClick={() => handleAiStaging('landscape')} disabled={stagingLoading}>🌳 Landscape</button>
              <button className="staging-type-button" onClick={() => handleAiStaging('room')} disabled={stagingLoading}>🏠 Room</button>
            </div>
            {stagingLoading && (
              <div className="staging-loading">
                <div className="loading-spinner"></div>
                <p>Generating AI staging...</p>
              </div>
            )}
            <button className="close-staging-modal" onClick={() => setShowAiStaging(false)}>Cancel</button>
          </div>
        </div>
      )}

      {showCopySuccess && (
        <div className="property-toast property-toast-success" role="status">
          Link copied to clipboard!
        </div>
      )}

    </>
  );
};

export default PropertyDetailModal;
